<script setup>
import { onMounted, onUnmounted, ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useGarageStore } from '@/stores/garage.js'
import { useModalStore } from '@/stores/modal.js'
import { useRequestAQuoteStore } from '@/stores/request_a_quote.js'
import SelectServiceOption from '@/components/vehicle/select_service_option.vue'
import { useIntelichekFetch } from '@/lib/intelichek-fetch.js'

const props = defineProps({
  service: {
    type: Object,
    required: true
  },
  index: {
    type: Number,
    required: true
  },
  isCategoriesLoading: {
    type: Boolean,
    required: true
  },
  categories: {
    type: Array,
    required: true
  }
})

// Stores
const garageStore = useGarageStore()
const modalStore = useModalStore()
const blitzQuoteStore = useRequestAQuoteStore()

// Variables
const { t } = useI18n()

const categoryList = computed(() => {
  // Map current category selections user made
  const selectedCategories = blitzQuoteStore.selectedServices.map(service => service.category);

  // Determine available categories
  return props.categories.filter(category => {
    // Check if this is an oil change service and vehicle doesn't take oil
    const isOilChangeService = category.name && category.name.toLowerCase().includes('oil');
    const vehicleHasNoOil = !garageStore.currentVehicle?.oil_capacity || garageStore.currentVehicle.oil_capacity === 0;
    
    // Exclude oil change services if vehicle doesn't take oil
    if (isOilChangeService && vehicleHasNoOil) {
      return false;
    }

    // Include category if it's the currently selected
    return (!selectedCategories.includes(category) || blitzQuoteStore.selectedServices[props.index - 1].category === category);
  });
});

// Queries
const categoryOptionsUrl = computed(() => {
  const url = new URL(`${import.meta.env.VITE_BACKEND_URL}/api/v2/servicechek/category_options`)
  url.searchParams.append('query[category_id_eq]', props.service.category.id)
  url.searchParams.append('query[archived_eq]', 'false')
  return url.href;
})
const {
  isFetching: isCategoryOptionsLoading,
  data: categoryOptions
} = useIntelichekFetch(categoryOptionsUrl, { refetch: true }).json()


// Events
onMounted(() => {
  // No event listeners needed for inline display
})

onUnmounted(() => {
  // No cleanup needed
})

watch(() => props.service.category, async () => {
  // Remove additional category option choices
  props.service.optionChoices.splice(1)
})

watch(() => props.categories, (newCategories) => {
  // Categories updated
}, { immediate: true })

// Service category icons mapping
const serviceIcons = {
  'Synthetic Oil And Filter Change': '🛢️',
  'Conventional Oil And Filter Change': '🛢️',
  'Synthetic Blend Oil And Filter Change': '🛢️',
  'Tire Rotation': '🚗',
  'Engine Air Filter': '💨',
  'Cabin Air Filter': '🌬️',
  'Multi Point Inspection': '🔍',
  'Check Engine Light Diagnosis': '⚠️',
  'Rotation And Balance': '🚗',
  '4 Wheel Alignment': '🚗'
}

// Functions
function removeService() {
  blitzQuoteStore.selectedServices.splice(props.index - 1, 1)
}

function resetCategory() {
  blitzQuoteStore.selectedServices[props.index - 1].category = null
  blitzQuoteStore.selectedServices[props.index - 1].optionChoices = [{}]
}

function selectCategory(category) {
  props.service.category = category
}

function getServiceIcon(categoryName) {
  return serviceIcons[categoryName] || '🔧'
}

</script>

<template>
  <div class="mt-4">
    <div class="flex flex-row">
      <p class="text-2xl font-semibold mb-2">{{ t('request_a_quote.service') }}</p>
    </div>
    <div class="w-[480px] flex flex-row">
      <p v-if="props.index !== 1" @click="removeService"
         class="cursor-pointer text-md mb-2 font-medium select-history mt-2 ml-8">
        {{ t('request_a_quote.service_section.remove_service') }}</p>
    </div>

    <div class="w-full mb-6 mt-4">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-semibold text-gray-900">{{ t('request_a_quote.service_section.choose_service_type') }}</h3>
        <button 
          v-if="props.service.category?.name"
          @click="resetCategory"
          class="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
        >
          {{ t('request_a_quote.service_section.clear') }}
        </button>
      </div>
      
      <!-- Service Category Tiles Grid -->
      <div v-if="!props.isCategoriesLoading" class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          v-for="category in categoryList"
          :key="category.id"
          @click="selectCategory(category)"
          :class="[
            'relative p-4 rounded-2xl cursor-pointer transition-all duration-200 flex flex-row items-center justify-start text-left min-h-[100px] border-2 service-tile',
            props.service.category?.id === category.id
              ? 'selected-tile'
              : 'unselected-tile'
          ]"
        >
          <!-- Service Icon -->
          <div class="text-4xl mr-3">
            {{ getServiceIcon(category.name) }}
          </div>
          
          <!-- Service Name -->
          <span class="text-sm font-bold leading-tight flex-1">
            {{ category.name }}
          </span>
          
          <!-- Selected Indicator -->
          <div v-if="props.service.category?.id === category.id" 
               class="absolute top-2 right-2 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
          </div>
        </button>
      </div>
      
      <!-- Skeleton Loading State -->
      <div v-else class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div
          v-for="n in 10"
          :key="n"
          class="skeleton-tile p-4 rounded-2xl min-h-[100px] border-2 flex flex-row items-center justify-start"
        >
          <!-- Skeleton Icon -->
          <div class="skeleton-icon w-10 h-10 rounded-full mr-3"></div>
          
          <!-- Skeleton Text -->
          <div class="skeleton-text h-4 rounded flex-1"></div>
        </div>
      </div>
    </div>

    <!-- Info message for vehicles without oil capacity -->
    <div v-if="!garageStore.currentVehicle?.oil_capacity || garageStore.currentVehicle.oil_capacity === 0" 
         class="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
      <p class="text-sm text-blue-700">
        <strong>Note:</strong> Oil change services are not available for this vehicle as it does not require engine oil.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-3 gap-x-4" v-if="categoryOptions?.data.length">
      <SelectServiceOption
        v-for="(optionChoice, index) in props.service.optionChoices"
        :key="index"
        :service-index="props.index"
        :index="index"
        :service="props.service"
        :option-choice="optionChoice"
        :is-category-options-loading="isCategoryOptionsLoading"
        :category-options="categoryOptions.data"
      />
    </div>

    <div v-if="props.index !== blitzQuoteStore.selectedServices.length" class="mt-8 mb-6 border-t"></div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/views/request_a_quote/request_a_quote';

.selected-pill {
  background: $primary-color;
  color: white;
}

.service-tile {
  background: linear-gradient(135deg, $primary-light-color 0%, rgba($secondary-color, 0.02) 100%);
  position: relative;
  
  span {
    text-shadow: 0 1px 2px rgba($primary-color, 0.1);
  }
  
  &.unselected-tile {
    color: $primary-color;
    border-color: rgba($secondary-color, 0.3);
    
    &:hover {
      border-color: rgba($secondary-color, 0.5);
      background: linear-gradient(135deg, $primary-light-color 0%, rgba($secondary-color, 0.03) 100%);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba($secondary-color, 0.15);
      
      span {
        text-shadow: 0 1px 3px rgba($primary-color, 0.15);
      }
    }
    
    &:active {
      transform: scale(0.96);
    }
  }
  
  &.selected-tile {
    color: $primary-color;
    border-color: transparent;
    font-weight: 700;
    background: linear-gradient(135deg, $primary-light-color 0%, rgba($secondary-color, 0.04) 100%);
    box-shadow: 0 4px 16px rgba($active-vehicle-border, 0.2);
    transform: scale(0.98);
    
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      border: 4px solid #b05b34;
      animation: outline-fill 0.8s ease-out forwards;
    }
    
    span {
      text-shadow: 0 1px 3px rgba($primary-color, 0.2);
    }
  }
}

@keyframes outline-fill {
  0% { clip-path: polygon(50% 0, 50% 0, 50% 100%, 50% 100%); }
  25% { clip-path: polygon(25% 0, 75% 0, 75% 100%, 25% 100%); }
  50% { clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%); }
  100% { clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%); }
}

.skeleton-tile {
  background: linear-gradient(135deg, $primary-light-color 0%, rgba($secondary-color, 0.01) 100%);
  border-color: rgba($secondary-color, 0.2);
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

.skeleton-icon {
  background: linear-gradient(90deg, rgba($secondary-color, 0.02) 25%, rgba($secondary-color, 0.04) 50%, rgba($secondary-color, 0.02) 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
}

.skeleton-text {
  width: 60%;
  background: linear-gradient(90deg, rgba($secondary-color, 0.02) 25%, rgba($secondary-color, 0.04) 50%, rgba($secondary-color, 0.02) 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
}

@keyframes skeleton-pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

@keyframes skeleton-shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}
</style>